/**
 * English for the console.
 *
 * Keyed by the German source string, so this is the only dictionary that has to exist —
 * see `lib/i18n.tsx` for why. Ordered by where the text appears, because that is how it
 * gets reviewed: someone reads one screen in English and fixes what reads wrong.
 *
 * Two rules for the translations themselves:
 *
 *   Legal terms keep their German name where the German name *is* the thing. "§ 25 TDDDG"
 *   has no English equivalent and translating it invents a law. GDPR/DSGVO is the
 *   exception — both names are current in English.
 *
 *   Nothing here is softened. The German console says a scan finding is a compliance gap
 *   and that a broken chain needs explaining; an English version that hedges those into
 *   "may need attention" is a different product.
 */
export const en: Record<string, string> = {
  // ── Shell ────────────────────────────────────────────────────────────────────────
  'Sitzung wird geprüft …': 'Checking your session …',
  'Konto wechseln': 'Switch account',
  'Ihre Rolle: {hint}': 'Your role: {hint}',
  'bestimmt, was Sie hier tun können': 'decides what you can do here',
  Abmelden: 'Sign out',
  Team: 'Team',
  Hilfe: 'Help',
  Sprache: 'Language',
  Deutsch: 'German',
  Englisch: 'English',

  // ── Help ─────────────────────────────────────────────────────────────────────────
  'Alle Hilfethemen': 'All help topics',
  Schließen: 'Close',
  'Hilfe: {topic}': 'Help: {topic}',
  'Jedes Thema steht auch dort, wo es gebraucht wird — hinter dem Fragezeichen neben der jeweiligen Überschrift.':
    'Every topic also sits where it is needed — behind the question mark next to the relevant heading.',

  // ── Shared controls (lib/ui.tsx, lib/Modal.tsx) ──────────────────────────────────
  Kopieren: 'Copy',
  'Site-Schlüssel': 'Site key',
  'Site-ID (API)': 'Site ID (API)',
  'Kopiert ✓': 'Copied ✓',
  'Bitte von Hand markieren': 'Please select it by hand',
  '{what} konnte nicht geladen werden.': '{what} could not be loaded.',
  'Erneut versuchen': 'Try again',
  Abbrechen: 'Cancel',
  Bestätigen: 'Confirm',
  Entfernen: 'Remove',

  // ── Roles, regimes, categories (lib/labels.ts) ───────────────────────────────────
  Inhaber: 'Owner',
  Administrator: 'Administrator',
  Redakteur: 'Editor',
  Auswertung: 'Analyst',
  'Darf alles, auch Team und Abrechnung.': 'May do everything, including team and billing.',
  'Darf Websites und Konfiguration, aber keine Inhaber ernennen.':
    'May manage websites and configuration, but cannot appoint owners.',
  'Darf bearbeiten, aber nicht veröffentlichen.': 'May edit, but not publish.',
  'Darf lesen und auswerten, nichts ändern.': 'May read and analyse, and change nothing.',

  'DSGVO + TDDDG': 'GDPR + TDDDG',
  DSGVO: 'GDPR',
  'revDSG (Schweiz)': 'revFADP (Switzerland)',
  'Kein Regime': 'No regime',
  'Deutschland: DSGVO und § 25 TDDDG': 'Germany: GDPR and § 25 TDDDG',
  'EU/EWR: DSGVO und ePrivacy': 'EU/EEA: GDPR and ePrivacy',
  'Schweiz: revidiertes Datenschutzgesetz': 'Switzerland: revised Data Protection Act',
  'Kalifornien: CCPA/CPRA': 'California: CCPA/CPRA',
  'Kein bekanntes Einwilligungsrecht': 'No known consent law',

  'Einwilligung vorab': 'Consent first',
  'Widerspruch möglich': 'Opt-out available',
  'Nur Hinweis': 'Notice only',

  Notwendig: 'Necessary',
  Funktional: 'Functional',
  Statistik: 'Analytics',
  Marketing: 'Marketing',

  Desktop: 'Desktop',
  Mobil: 'Mobile',
  Tablet: 'Tablet',
  Bot: 'Bot',
  Unbekannt: 'Unknown',

  'Besucher aus Deutschland': 'Visitor from Germany',
  'Besucher aus der EU / dem EWR': 'Visitor from the EU / EEA',
  'Besucher aus der Schweiz': 'Visitor from Switzerland',
  'US-Bundesstaat mit eigenem Datenschutzgesetz': 'US state with its own privacy law',
  'USA, Bundesstaat ohne eigenes Gesetz': 'USA, state without its own law',
  'Land ohne Einwilligungspflicht': 'Country with no consent requirement',
  'Land unbekannt — dann gilt das strengste Regime':
    'Country unknown — the strictest regime applies',

  'aus Scan': 'from scan',
  'von Hand': 'by hand',
  Vorgabe: 'Built-in',

  // ── Websites (routes/Sites.tsx, routes/SiteDetail.tsx) ───────────────────────────
  Websites: 'Websites',
  'Website hinzufügen': 'Add website',
  'Noch keine Website': 'No website yet',
  'Eine Website ist eine Domain mit einem eigenen Banner. Nach dem Anlegen bekommen Sie ein Snippet für den <head>.':
    'A website is one domain with its own banner. Once created, you get a snippet for your <head>.',
  'Ihre Rolle darf keine Website anlegen. Bitten Sie jemanden mit der Rolle „admin“ oder „owner“ darum.':
    'Your role cannot create a website. Ask someone with the "admin" or "owner" role.',
  'Erste Website anlegen': 'Create the first website',
  'Die Websites': 'The websites',
  live: 'live',
  'nie veröffentlicht': 'never published',
  'seit {date}': 'since {date}',

  '← Websites': '← Websites',
  Banner: 'Banner',
  'Blockieren & Anbieter': 'Blocking & vendors',
  Nachweise: 'Proofs',
  Bereiche: 'Sections',
  'Diese Website gibt es nicht (mehr).': 'This website does not exist (any more).',
  'Zur Übersicht': 'Back to the list',
  'Für diese Website haben Sie keine Berechtigung.':
    'You have no permission for this website.',
  'Diese Website': 'This website',

  // ── Blocking: legal regime and rules (features/blocking/BlockingScreen.tsx) ──────
  'Die geltenden Regeln': 'The effective rules',
  Rechtslage: 'Legal regime',
  Regime: 'Regime',
  Grundlage: 'Legal basis',
  'Ablehnen-Button': 'Reject button',
  Verpflichtend: 'Mandatory',
  Optional: 'Optional',
  'Einwilligung gilt': 'Consent lasts',
  '{n} Tage': '{n} days',
  'Regeln aktiv': 'Active rules',
  Website: 'Website',
  Regeln: 'Rules',
  'Regel hinzufügen': 'Add rule',
  'Unbekannt — die geltenden Regeln konnten nicht geladen werden.':
    'Unknown — the effective rules could not be loaded.',
  'Suchen — z. B. doubleclick': 'Search — e.g. doubleclick',
  'Regeln durchsuchen': 'Search rules',
  'Alle {n}': 'All {n}',
  'Keine Regel enthält „{needle}“.': 'No rule contains "{needle}".',
  'Nichts.': 'Nothing.',
  eigene: 'own',
  Plattform: 'Platform',
  'Regel entfernen?': 'Remove rule?',
  '{value} wird ab der nächsten Veröffentlichung nicht mehr ausgenommen.':
    'From the next publish onwards, {value} will no longer be exempted.',
  '{value} wird ab der nächsten Veröffentlichung nicht mehr angehalten.':
    'From the next publish onwards, {value} will no longer be held back.',
  'Zum Veröffentlichen': 'Go publish',
  'Regeln werden im Banner-Editor veröffentlicht — dort steht der Knopf für die ganze Website.':
    'Rules are published in the banner editor — the button there covers the whole site.',
  'Plattform-Regeln gelten immer. Eigene kommen additiv dazu — erlauben gewinnt gegen blockieren, und Änderungen wirken erst nach dem nächsten Veröffentlichen.':
    'Platform rules always apply. Your own are additive — allow beats block, and changes take effect at the next publish.',
  'Fehlgeschlagen.': 'Failed.',
  Art: 'Kind',
  Treffer: 'Match',
  Wert: 'Value',
  Kategorie: 'Category',
  Hinzufügen: 'Add',
  'Dieser reguläre Ausdruck kann den Browser des Besuchers blockieren (verschachtelte Quantoren).':
    'This regular expression can lock up the visitor\'s browser (nested quantifiers).',
  'Klammern im Ausdruck sind unausgeglichen.': 'The brackets in the expression are unbalanced.',
  'Der Ausdruck ist zu lang (max. 200 Zeichen).':
    'The expression is too long (200 characters maximum).',
  'Diese Änderung würde eine DSGVO-Garantie abschwächen und wird nicht gespeichert.':
    'This change would weaken a GDPR guarantee and will not be saved.',
  'Regel ist nicht gültig.': 'The rule is not valid.',

  // Rule kinds and match types (translated where they render, see KIND / MATCH).
  Blockieren: 'Block',
  Erlauben: 'Allow',
  Cookie: 'Cookie',
  'Klick-zum-Laden': 'Click-to-load',
  Ausnahme: 'Exception',
  Domain: 'Domain',
  'Regulärer Ausdruck': 'Regular expression',
  'Im Seitentext': 'In the page text',
  'Beginnt mit': 'Starts with',
  Genau: 'Exactly',

  // ── Blocking: vendors ────────────────────────────────────────────────────────────
  Anbieter: 'Vendors',
  'Anbieter hinzufügen': 'Add vendor',
  'Art. 13 verlangt, dass die Empfänger benannt werden. Diese Liste erscheint im Auswahl-Dialog, sobald sie dort eingeschaltet ist.':
    'Art. 13 requires that recipients be named. This list appears in the preferences dialog once it is switched on there.',
  'Noch keine Anbieter gepflegt.': 'No vendors maintained yet.',
  Bearbeiten: 'Edit',
  'Anbieter entfernen?': 'Remove vendor?',
  '{name} verschwindet aus dem Auswahl-Dialog. Wenn er weiterhin geladen wird, fehlt er dort als Empfänger.':
    '{name} disappears from the preferences dialog. If it still loads, it is missing there as a recipient.',
  Name: 'Name',
  Schlüssel: 'Key',
  'Bleibt stabil, auch wenn der Name sich ändert.': 'Stays stable even when the name changes.',
  Speicherdauer: 'Retention',
  '14 Monate': '14 months',
  Datenschutzerklärung: 'Privacy policy',
  Cookies: 'Cookies',
  'Komma-getrennt.': 'Comma-separated.',
  'Speichern fehlgeschlagen. Schlüssel schon vergeben?': 'Saving failed. Is the key already taken?',
  Speichern: 'Save',

  // ── Cookie declaration (features/blocking/DeclarationCard.tsx) ───────────────────
  'Cookie-Erklärung': 'Cookie declaration',
  'Als HTML kopieren': 'Copy as HTML',
  'Für Ihre Datenschutzerklärung. Sie wird aus den veröffentlichten Anbietern erzeugt und ändert sich mit jeder Veröffentlichung — was von Hand gepflegte Listen nie tun.':
    'For your privacy policy. It is generated from the published vendors and changes with every publish — which hand-maintained lists never do.',
  'Die Cookie-Erklärung': 'The cookie declaration',
  'Noch nichts veröffentlicht — es gibt daher noch nichts zu erklären.':
    'Nothing published yet — so there is nothing to declare.',
  Dauer: 'Duration',
  Details: 'Details',
  'Wer sie lieber selbst rendert, holt dieselben Daten hier:':
    'To render it yourself, the same data is here:',
  'Eingesetzte Cookies und Dienste auf {site}': 'Cookies and services in use on {site}',

  // ── Domains (features/blocking/DomainsCard.tsx) ──────────────────────────────────
  Domains: 'Domains',
  'Nur diese Hosts dürfen das Banner laden und Ereignisse melden. Als Testumgebung markierte Domains zeigen das Banner mit einem Hinweis und melden nichts — die Auswertung bleibt sauber.':
    'Only these hosts may load the banner and report events. Domains marked as a test environment show the banner with a notice and report nothing — your analytics stay clean.',
  'Die Domains': 'The domains',
  Hauptdomain: 'Primary domain',
  Testumgebung: 'Test environment',
  'Domain entfernen?': 'Remove domain?',
  'Auf {domain} lädt das Banner danach nicht mehr, und Ereignisse von dort werden abgewiesen.':
    'The banner will no longer load on {domain}, and events from there will be rejected.',
  'Diese Domain gehört bereits zu einer Website.': 'This domain already belongs to a website.',
  'Die Domain sieht nicht richtig aus — nur der Host, ohne https:// und ohne Pfad.':
    'That domain does not look right — the host only, no https:// and no path.',
  'Die Domain konnte nicht entfernt werden.': 'The domain could not be removed.',

  // ── Scan (features/scan/ScanScreen.tsx) ───────────────────────────────────────
  'Website scannen': 'Scan website',
  'Lädt die Seite zweimal: einmal ohne Einwilligung, einmal mit. Die erste Runde zeigt, was trotz Banner feuert. Anfragen an Anbieter werden dabei beobachtet und im Browser gestoppt — der Scan landet nicht als Besuch in Ihrer Statistik.':
    'Loads the page twice: once without consent, once with. The first pass shows what fires despite the banner. Requests to vendors are observed and stopped in the browser — the scan does not land in your analytics as a visit.',
  'Start-URL': 'Start URL',
  'läuft …': 'running …',
  'Scan starten': 'Start scan',
  'Noch kein Scan gelaufen.': 'No scan has run yet.',
  'Scan fehlgeschlagen: {error}': 'Scan failed: {error}',
  '{n} Seite geprüft': '{n} page checked',
  '{n} Seiten geprüft': '{n} pages checked',
  '{n} Anfragen beobachtet': '{n} requests observed',
  '{n} Cookies': '{n} cookies',
  '{n} gestoppt': '{n} stopped',
  'Vor der Einwilligung geladen ({n})': 'Loaded before consent ({n})',
  'Vor der Einwilligung wurde kein Drittanbieter geladen.':
    'No third party was loaded before consent.',
  'Nach der Einwilligung ({n})': 'After consent ({n})',
  'Vorschläge ({n})': 'Proposals ({n})',
  'Nacheinander durchgehen': 'Go through one by one',
  'Der Scanner entscheidet nichts. Übernommene Anbieter erscheinen im Auswahl-Dialog. Solange ein Vorschlag offen ist, fehlt der Anbieter dort als Empfänger.':
    'The scanner decides nothing. Adopted vendors appear in the preferences dialog. While a proposal is open, that vendor is missing there as a recipient.',
  'nicht erkannt': 'not recognised',
  erkannt: 'recognised',
  'Cookies: {list}': 'Cookies: {list}',
  'Seiten: {list}': 'Pages: {list}',
  'Scan konnte nicht gestartet werden.': 'The scan could not be started.',
  'Für diese Website läuft bereits ein Scan.': 'A scan is already running for this website.',
  'Diese Adresse gehört nicht zu den bestätigten Domains dieser Website.':
    'That address is not one of this website\'s confirmed domains.',
  'Nur http:// und https:// sind möglich.': 'Only http:// and https:// are possible.',
  'Wöchentlich automatisch scannen': 'Scan automatically every week',
  'Zuletzt {when}.': 'Last run {when}.',
  'Eine Mail geht nur raus, wenn neue Anbieter auftauchen — an Inhaber und Administratoren dieses Kontos.':
    'Mail goes out only when new vendors appear — to the owners and administrators of this account.',

  // ── Review queue (features/scan/ReviewQueue.tsx) ─────────────────────────────
  'Die Entscheidung konnte nicht gespeichert werden.': 'The decision could not be saved.',
  Fertig: 'Done',
  'Vorschlag {i} von {n}': 'Proposal {i} of {n}',
  Später: 'Later',
  Verwerfen: 'Discard',
  Übernehmen: 'Adopt',
  'Wird übernommen …': 'Adopting …',
  'Alle Vorschläge sind entschieden. Übernommene Anbieter erscheinen im Auswahl-Dialog, sobald Sie das nächste Mal veröffentlichen.':
    'Every proposal has been decided. Adopted vendors appear in the preferences dialog the next time you publish.',
  'Gefunden als': 'Found as',
  'Die Einordnung entscheidet, ob dieser Anbieter vor einer Einwilligung geladen werden darf. „Notwendig“ heißt: immer erlaubt.':
    'The categorisation decides whether this vendor may load before consent. "Necessary" means: always allowed.',
  'Notwendig — ohne das funktioniert die Seite nicht':
    'Necessary — the site does not work without it',
  'Funktional — Komfort, Karten, Chat': 'Functional — convenience, maps, chat',
  'Statistik — Reichweite und Nutzung': 'Analytics — reach and usage',
  'Marketing — Werbung und Retargeting': 'Marketing — advertising and retargeting',
  'Übernehmen nimmt den Anbieter in die Liste auf, die im Auswahl-Dialog genannt wird. Verwerfen blendet ihn aus — er wird dann bei künftigen Scans nicht erneut vorgeschlagen.':
    'Adopt puts the vendor into the list named in the preferences dialog. Discard hides it — future scans will not propose it again.',

  // ── Banner editor: draft state and publishing (features/banner/Builder.tsx) ──────
  'Der Entwurf': 'The draft',
  Entwurf: 'Draft',
  'wird gespeichert …': 'saving …',
  'gespeichert {time}': 'saved {time}',
  ungespeichert: 'unsaved',
  Konflikt: 'Conflict',
  'Speichern fehlgeschlagen': 'Saving failed',
  'Automatisches Speichern fehlgeschlagen.': 'Autosave failed.',
  'Diese Konfiguration wurde an anderer Stelle geändert.':
    'This configuration was changed elsewhere.',
  'Erneut speichern': 'Save again',
  oder: 'or',
  'den Serverstand laden': 'load the server version',
  'Serverstand laden?': 'Load the server version?',
  'Serverstand laden': 'Load server version',
  'Ihre nicht gespeicherten Änderungen an diesem Banner gehen dabei verloren, einschließlich der Rückgängig-Schritte. Das lässt sich nicht widerrufen.':
    'Your unsaved changes to this banner will be lost, undo history included. This cannot be reversed.',
  'Sie haben für diese Website nur Lesezugriff. Änderungen hier werden nicht gespeichert — fragen Sie jemanden mit der Rolle „editor“ oder höher.':
    'You have read-only access to this website. Changes here are not saved — ask someone with the "editor" role or higher.',
  'So kann nicht veröffentlicht werden:': 'This cannot be published as it stands:',
  'Rückgängig: {what}': 'Undo: {what}',
  'Nichts rückgängig zu machen': 'Nothing to undo',
  Wiederholen: 'Redo',
  'Sprache der Texte': 'Language of the texts',
  'Sprachcode, zwei Buchstaben, z. B. en': 'Language code, two letters, e.g. en',
  '„{code}“ ist kein zweibuchstabiger Sprachcode. Beispiele: en, fr, it.':
    '"{code}" is not a two-letter language code. Examples: en, fr, it.',
  '+ Sprache …': '+ Language …',
  'Es gibt offene Probleme': 'There are open problems',
  'Nicht veröffentlichbar': 'Cannot be published',
  'Erneut veröffentlichen': 'Publish again',
  'Am Banner ändert sich nichts. Regeln, Anbieter und Ausnahmen stehen nicht im Entwurf — erst das Veröffentlichen liefert sie aus. (⌘↵)':
    'Nothing changes about the banner. Rules, vendors and their exceptions are not part of the draft — only publishing ships them. (⌘↵)',
  'Am Banner ändert sich nichts. Regeln, Anbieter und Ausnahmen stehen nicht im Entwurf und werden hier nicht verglichen — sie werden beim Veröffentlichen neu gebaut, und genau so kommt eine geänderte Blockier-Regel zu den Besuchern.':
    'Nothing changes about the banner. Rules, vendors and their exceptions are not part of the draft and are not compared here — they are rebuilt on publishing, and that is exactly how a changed blocking rule reaches visitors.',
  'Veröffentlichen · {n}': 'Publish · {n}',
  '{n} Änderung veröffentlichen (⌘↵)': 'Publish {n} change (⌘↵)',
  '{n} Änderungen veröffentlichen (⌘↵)': 'Publish {n} changes (⌘↵)',
  'Veröffentlichen: keine Berechtigung': 'Publishing: no permission',
  'Bearbeiten dürfen Sie, veröffentlichen dürfen Admin und Owner.':
    'You may edit; admins and owners may publish.',
  Veröffentlichen: 'Publish',
  'Veröffentlichen …': 'Publishing …',
  'Jetzt veröffentlichen': 'Publish now',
  'Danach sehen Besucher diese Fassung. Die vorherige Version bleibt abrufbar.':
    'Visitors will see this version afterwards. The previous one stays retrievable.',
  'Die aktuell veröffentlichte Fassung konnte nicht geladen werden, deshalb lässt sich hier nicht zeigen, was sich ändert.':
    'The currently published version could not be loaded, so what changes cannot be shown here.',
  'Erste Veröffentlichung. Ab jetzt lädt das SDK diese Konfiguration statt der eingebauten Notfall-Regeln.':
    'First publish. From now on the SDK loads this configuration instead of its built-in emergency rules.',
  'Ihre Rolle darf nicht veröffentlichen. „editor“ darf bearbeiten, veröffentlichen dürfen Admin und Owner.':
    'Your role may not publish. "editor" may edit; admins and owners may publish.',
  'Die Konfiguration hat noch offene Probleme.': 'The configuration still has open problems.',
  'Fehlgeschlagen ({status}).': 'Failed ({status}).',
  'Veröffentlicht als {version}. Besucher sehen die neue Fassung in wenigen Minuten.':
    'Published as {version}. Visitors will see the new version within a few minutes.',
  'Bundle über 12 kB gzip.': 'Bundle over 12 kB gzip.',

  // Preview strip
  'auf Ihrer Seite': 'on your page',
  'Seitenbild wird geholt …': 'fetching page image …',
  Platzhalterseite: 'placeholder page',
  'Entwurf — noch nicht veröffentlicht': 'Draft — not published yet',
  'Veröffentlichte Fassung': 'Published version',
  'Auswahl-Dialog': 'Preferences dialog',
  Anpassen: 'Fit',
  'Bedienfeld ausblenden': 'Hide panel',
  'Bedienfeld einblenden': 'Show panel',
  'Größe der ausgelieferten Konfiguration (Grenze 12 288 B gzip)':
    'Size of the delivered configuration (limit 12,288 B gzip)',
  '{pct} % des Größenlimits': '{pct} % of the size limit',
  'Größe in Ordnung': 'Size is fine',

  // Install card
  Einbau: 'Installation',
  'Wird geprüft …': 'Checking …',
  'Jetzt prüfen': 'Check now',
  'Noch nicht geprüft. Der Test lädt {domain} und sieht nach, ob der Loader da ist und ob vor der Einwilligung etwas feuert.':
    'Not checked yet. The test loads {domain} and looks for the loader, and for anything firing before consent.',
  'Nicht prüfbar': 'Cannot be checked',
  Eingebaut: 'Installed',
  'Nicht in Ordnung': 'Not in order',
  'geprüft {when}': 'checked {when}',
  'Seite erreichbar': 'Page reachable',
  'Loader gefunden': 'Loader found',
  'Nicht geprüft — die Seite war nicht erreichbar.':
    'Not checked — the page was not reachable.',
  'Auf der Startseite steht kein Snippet.': 'There is no snippet on the home page.',
  'Richtiger Site-Schlüssel': 'Correct site key',
  'Nicht geprüft.': 'Not checked.',
  'Es lädt ein Loader einer anderen Website.': 'A loader for a different website is loading.',
  'Nichts vor der Einwilligung': 'Nothing before consent',
  '{vendors} — das Snippet steht zu weit unten im <head>.':
    '{vendors} — the snippet sits too low in the <head>.',
  'Snippet ausblenden': 'Hide snippet',
  'Snippet anzeigen': 'Show snippet',

  // History
  Verlauf: 'History',
  'Noch nichts veröffentlicht.': 'Nothing published yet.',
  'von {who}': 'by {who}',
  'Textprüfsumme {hash}': 'Text checksum {hash}',
  'Diese Fassung wieder live schalten?': 'Put this version live again?',
  Zurückrollen: 'Roll back',
  'Besucher sehen ab sofort wieder die Fassung vom {when}. Ihr Entwurf bleibt unverändert — der Editor bietet danach weiter an, ihn zu veröffentlichen.':
    'Visitors will immediately see the version from {when} again. Your draft is untouched — the editor will still offer to publish it.',
  'Ihre Rolle darf nicht veröffentlichen und damit auch nicht zurückrollen.':
    'Your role may not publish, and therefore may not roll back either.',
  'Zurückrollen fehlgeschlagen.': 'Rolling back failed.',

  // ── Guardrail nudges (features/banner/guardrails/darkPattern.ts) ─────────────────
  'Ohne „Einstellungen“ gibt es keinen Weg zur granularen Auswahl — Einwilligung muss granular möglich sein.':
    'Without "Settings" there is no route to the granular choice — consent has to be possible granularly.',
  'Ablehnen als Link neben Akzeptieren als Button ist der klassische Dark-Pattern-Vorwurf.':
    'Reject as a link beside accept as a button is the classic dark-pattern accusation.',
  'Akzeptieren steht vor Ablehnen. Screenreader und Tastatur erreichen damit die Zustimmung zuerst — erlaubt, aber die Anordnung, die am ehesten beanstandet wird.':
    'Accept comes before reject. Screen readers and keyboards therefore reach agreement first — permitted, but the arrangement most likely to be challenged.',
  'Ohne Badge oder eigenen Link ist der Widerruf nicht so einfach wie die Einwilligung.':
    'Without a badge or your own link, withdrawing is not as easy as consenting.',
  'Alle optionalen Kategorien sind vorausgewählt. Unter Opt-in ist das keine freiwillige Einwilligung.':
    'Every optional category is pre-selected. Under opt-in that is not freely given consent.',
  'Ohne Übersetzung: {langs} — Besucher mit dieser Spracheinstellung sehen den deutschen Text.':
    'Untranslated: {langs} — visitors with that language setting see the German text.',

  // ── Banner editor: property panels (features/banner/panels/PropsPanel.tsx) ───────
  'Titel & Text · {lang}': 'Title & text · {lang}',
  Überschrift: 'Heading',
  Text: 'Body',
  'Dieser Wortlaut wird im Nachweis festgehalten.': 'This wording is recorded in the proof.',
  'Layout & Position': 'Layout & position',
  'Farben & Form': 'Colours & shape',
  Overlay: 'Overlay',
  'Die Abdunklung hinter dem Dialog.': 'The dimming behind the dialog.',
  'Ecken-Radius': 'Corner radius',
  Schrift: 'Type',
  Schriftart: 'Typeface',
  Hausschrift: 'House font',
  'Nur ein Schriftname — keine Adresse, kein url(), kein @import.':
    'A font name only — no address, no url(), no @import.',
  Schriftgröße: 'Type size',
  Buttons: 'Buttons',
  Anordnung: 'Arrangement',
  '„Untereinander“ macht jeden Button so breit wie der Dialog — auch im Auswahl-Dialog.':
    '"Stacked" makes every button as wide as the dialog — in the preferences dialog too.',
  'Reihenfolge im Banner': 'Order in the banner',
  'Reihenfolge im Auswahl-Dialog': 'Order in the preferences dialog',
  'Der Dialog hinter „Einstellungen“. „Auswahl speichern“ steht dort immer — es ist der einzige Weg, eine getroffene Auswahl festzuhalten.':
    'The dialog behind “Settings”. “Save selection” is always there — it is the only way to record a choice that was actually made.',
  'immer im Auswahl-Dialog': 'always in the preferences dialog',
  'im Banner': 'in the banner',
  'im Auswahl-Dialog': 'in the preferences dialog',
  '{button} nach oben': 'Move {button} up',
  '{button} nach unten': 'Move {button} down',
  'Buttons über die volle Breite': 'Full-width buttons',
  'Nebeneinander teilen sie sich die Zeile zu gleichen Teilen, untereinander füllt jeder sie ganz. Gleiche Breite hält Ablehnen und Akzeptieren nebenbei auch optisch gleich.':
    'Side by side they split the row evenly; stacked, each one fills it. Equal width also keeps reject and accept visually equal.',
  'Widerruf-Badge': 'Withdrawal badge',
  Modus: 'Mode',
  'Der Widerruf muss so einfach sein wie die Einwilligung.':
    'Withdrawing has to be as easy as consenting.',
  'Keins (eigener Link nötig)': 'None (your own link required)',
  Position: 'Position',
  'Ohne Badge müssen Sie selbst ein Element mit data-cmp-open platzieren,':
    'Without the badge you have to place an element carrying data-cmp-open yourself,',
  'Rechtliche Links · {lang}': 'Legal links · {lang}',
  'Link hinzufügen': 'Add link',
  'Ohne Link zur Datenschutzerklärung fehlt dem Banner die Information, auf die sich die Einwilligung bezieht.':
    'Without a link to the privacy policy, the banner is missing the information the consent refers to.',
  Logo: 'Logo',
  'Bild-URL': 'Image URL',
  Alternativtext: 'Alt text',
  'Leer lassen, wenn das Logo rein dekorativ ist.': 'Leave empty if the logo is purely decorative.',
  Höhe: 'Height',
  Ausrichtung: 'Alignment',
  'Größe & Abstände': 'Size & spacing',
  Breite: 'Width',
  'Gilt für Box und Modal. Leisten laufen immer über die volle Breite.':
    'Applies to box and modal. Bars always run the full width.',
  Innenabstand: 'Padding',
  'Abstand zwischen Elementen': 'Gap between elements',
  'Button-Anordnung': 'Button arrangement',
  'Overlay & Schließen': 'Overlay & dismissal',
  'Gesperrt, ohne Ablehnen-Button und ohne Schließen-Kreuz: Das ist eine Cookie-Wall. Veröffentlichen schlägt fehl.':
    'Locked, with no reject button and no close cross: that is a cookie wall. Publishing will fail.',
  'Schließen-Kreuz anzeigen': 'Show close cross',
  'Ein Klick darauf bedeutet': 'A click on it means',
  'Escape-Taste': 'Escape key',
  'Klick auf den Hintergrund': 'Click on the background',
  Einblendung: 'Entrance',
  'Auswahl-Dialog · {lang}': 'Preferences dialog · {lang}',
  Darstellung: 'Presentation',
  Einleitung: 'Introduction',
  '{consent_id} wird durch die Kennung dieser Einwilligung ersetzt — damit kann ein Besucher bei einer Auskunftsanfrage genau seinen Datensatz benennen.':
    '{consent_id} is replaced by the reference for this consent — so a visitor making a subject access request can name their exact record.',
  'Beschriftung für nicht abwählbare Kategorien': 'Label for categories that cannot be unticked',
  'Beschriftung des Schließen-Kreuzes': 'Label for the close cross',
  'Anbieter je Kategorie auflisten': 'List vendors per category',
  '{n} Anbieter gepflegt. Art. 13 verlangt, dass Empfänger benannt werden — im Dialog ist das der kürzeste Weg dorthin.':
    '{n} vendors maintained. Art. 13 requires recipients to be named — the dialog is the shortest route to that.',
  'Nicht gefunden.': 'Not found.',
  'Kategorie · {slug}': 'Category · {slug}',
  Beschreibung: 'Description',
  'Immer aktiv.': 'Always active.',
  '„necessary“ deckt ab, was für den Betrieb der Seite nötig ist, und ist per Gesetz nicht abwählbar.':
    '"necessary" covers what the site needs to operate, and by law cannot be unticked.',
  vorausgewählt: 'pre-selected',
  'Vorausgewählt heißt: der Schalter steht im Dialog auf an. Unter Opt-in wird trotzdem nichts geladen, bevor jemand zustimmt.':
    'Pre-selected means the switch shows as on in the dialog. Under opt-in nothing loads before someone agrees regardless.',
  'Kategorie entfernen': 'Remove category',
  'Eigene Kategorie hinzufügen': 'Add your own category',
  'Kleinbuchstaben, Ziffern, Unterstrich.': 'Lowercase, digits, underscore.',
  Fläche: 'Surface',

  // Layout presets, fonts, dismissal and button vocabulary (translated at render).
  'Leiste unten': 'Bar, bottom',
  'Leiste oben': 'Bar, top',
  'Box unten rechts': 'Box, bottom right',
  'Box unten links': 'Box, bottom left',
  'Box oben rechts': 'Box, top right',
  'Box oben links': 'Box, top left',
  'Modal mittig': 'Modal, centred',
  'System — wie das Betriebssystem': 'System — as the operating system',
  'Wie die Website': 'As the website',
  'Grotesk — Helvetica, Arial': 'Grotesque — Helvetica, Arial',
  'Serif — Georgia': 'Serif — Georgia',
  Monospace: 'Monospace',
  'nur schließen': 'close only',
  'alles ablehnen': 'reject everything',
  'nichts tun': 'do nothing',
  'Alle akzeptieren': 'Accept all',
  'Alle ablehnen': 'Reject all',
  Einstellungen: 'Settings',
  'Auswahl speichern': 'Save choice',
  Hervorgehoben: 'Prominent',
  'Bei gleicher Gewichtung teilen sich Ablehnen und Akzeptieren Stil und Farbe.':
    'Under equal weight, reject and accept share their style and colours.',
  Umrandet: 'Outlined',
  'Als Link': 'As a link',
  nebeneinander: 'side by side',
  untereinander: 'stacked',
  'außen verteilt': 'spread apart',
  links: 'left',
  mittig: 'centred',
  rechts: 'right',
  einfahren: 'slide in',
  einblenden: 'fade in',
  ohne: 'none',

  // ── Structure panel (features/banner/panels/TreePanel.tsx) ───────────────────────
  Struktur: 'Structure',
  Inhalt: 'Content',
  'Titel & Text': 'Title & text',
  'Rechtliche Links': 'Legal links',
  'Kategorien ({n})': 'Categories ({n})',
  Verhalten: 'Behaviour',
  'Diese Kategorie ist immer aktiv und lässt sich nicht abwählen.':
    'This category is always active and cannot be unticked.',
  'immer aktiv': 'always active',
  '{label} verschieben': 'Move {label}',
  'Ziehen zum Umsortieren': 'Drag to reorder',

  // ── Publish diff (features/banner/publish/DiffView.tsx) ──────────────────────────
  'Überschrift ({lang})': 'Heading ({lang})',
  'Text ({lang})': 'Body ({lang})',
  Layout: 'Layout',
  'Theme · {key}': 'Theme · {key}',
  Gleichgewichtung: 'Equal weight',
  Badge: 'Badge',
  'Identisch mit der aktuell veröffentlichten Fassung.':
    'Identical to the version currently published.',
  '{n} inhaltliche Änderung.': '{n} material change.',
  '{n} inhaltliche Änderungen.': '{n} material changes.',
  'Damit ändert sich der Nachweis-Hash, und alle Besucher mit bestehender Einwilligung werden erneut gefragt.':
    'That alters the proof hash, and every visitor with an existing consent is asked again.',
  'Ändert den Nachweis': 'Alters the proof',
  'Rechtliche Links ({lang})': 'Legal links ({lang})',
  'Titel des Auswahl-Dialogs ({lang})': 'Preferences dialog title ({lang})',
  'Einleitung des Auswahl-Dialogs ({lang})': 'Preferences dialog introduction ({lang})',
  'Beschriftung „immer aktiv“ ({lang})': 'Label for "always active" ({lang})',
  'Beschriftung des Schließen-Kreuzes ({lang})': 'Label for the close cross ({lang})',
  'Google Consent Mode · {key}': 'Google Consent Mode · {key}',
  Kategorien: 'Categories',
  Sprachen: 'Languages',
  'Schließen-Kreuz': 'Close cross',
  Titel: 'Title',
  Adresse: 'URL',
  Aktion: 'Action',
  Abstand: 'Gap',
  Reihenfolge: 'Order',
  Anbieterliste: 'Vendor list',
  'Cookie-Namen': 'Cookie names',
  Schriftfamilie: 'Font family',
  'Seite sperren': 'Lock the page',
  Pflicht: 'Required',
  ja: 'yes',
  nein: 'no',
  vorher: 'before',
  neu: 'now',

  // ── Proofs (features/proofs/ProofsScreen.tsx) ────────────────────────────────────
  'Nachweis suchen': 'Find a proof',
  'Eine Einwilligungs-ID oder ein Subjekt-Hash. Die ID kann im Auswahl-Dialog angezeigt werden — dann bringt eine Auskunftsanfrage sie meist gleich mit.':
    'A consent ID or a subject hash. The ID can be shown in the preferences dialog — a subject access request then usually arrives carrying it.',
  'Einwilligungs-ID oder Subjekt-Hash': 'Consent ID or subject hash',
  Suchen: 'Search',
  'Der Nachweis': 'The proof',
  'Zu {query} gibt es auf dieser Website keinen Beleg. Gehört die ID zu einer anderen Website dieses Kontos, suchen Sie dort — Belege sind pro Website getrennt.':
    'There is no record for {query} on this website. If the ID belongs to another website in this account, search there — records are kept per website.',
  Entscheidungen: 'Decisions',
  'Als JSON sichern': 'Save as JSON',
  Unversehrtheit: 'Integrity',
  'Die Belege {from}–{to} folgen lückenlos auseinander ({checked} geprüft). Jede nachträgliche Änderung an einem davon würde hier auffallen.':
    'Records {from}–{to} follow on from each other without a gap ({checked} verified). Any later change to one of them would show up here.',
  'Die Kette bricht bei Beleg {at}: {reason}. Das heißt nicht, dass dieser Nachweis falsch ist — es heißt, dass die Reihe um ihn herum nicht mehr geschlossen ist und der Grund geklärt werden sollte.':
    'The chain breaks at record {at}: {reason}. That does not mean this proof is wrong — it means the series around it is no longer closed, and the reason should be established.',
  'Geprüft wird durch Neuberechnung derselben Prüfsummen, die beim Schreiben entstanden sind — mit demselben Code, nicht mit einer zweiten Umsetzung davon.':
    'Verification recomputes the same checksums that were produced on write — with the same code, not a second implementation of it.',
  'Erste Entscheidung': 'First decision',
  'Änderung {n}': 'Change {n}',
  '{category}: erlaubt': '{category}: allowed',
  '{category}: abgelehnt': '{category}: refused',
  'Was gezeigt wurde': 'What was shown',
  'Der Wortlaut dieser Fassung ({version}) liegt nicht mehr vor. Die Entscheidung selbst bleibt gültig; die Prüfsumme des Textes ist festgehalten.':
    'The wording of this version ({version}) is no longer available. The decision itself remains valid; the checksum of the text is on record.',
  Seite: 'Page',
  Fassung: 'Version',
  '{version} · Regelwerk {policy}': '{version} · policy {policy}',
  Textprüfsumme: 'Text checksum',
  Subjekt: 'Subject',
  Beleg: 'Record',
  'Alles akzeptiert': 'Accepted everything',
  'Alles abgelehnt': 'Refused everything',
  'Auswahl gespeichert': 'Saved a selection',
  Widerrufen: 'Withdrawn',

  // ── Analytics (features/stats/StatsScreen.tsx) ───────────────────────────────────
  '7 Tage': '7 days',
  '30 Tage': '30 days',
  '90 Tage': '90 days',
  'Bots ausgenommen': 'bots excluded',
  'Die Auswertung': 'The analytics',
  'Noch keine Entscheidungen in diesem Zeitraum. Die Auswertung füllt sich, sobald Besucher das Banner sehen — die Aggregate werden beim Eintreffen gebildet, nicht rückwirkend.':
    'No decisions in this period yet. The analytics fill up once visitors see the banner — aggregates are built as events arrive, not retroactively.',
  Einwilligungsrate: 'Consent rate',
  'wird geladen …': 'loading …',
  'noch keine Entscheidungen': 'no decisions yet',
  '{optIn} von {all} Entscheidungen': '{optIn} of {all} decisions',
  'Banner gezeigt': 'Banner shown',
  'das Banner wurde noch nie ausgeliefert': 'the banner has never been delivered',
  '{n} unterschiedliche Besucher': '{n} distinct visitors',
  Entscheidungsquote: 'Decision rate',
  'Anteil der gezeigten Banner mit Entscheidung':
    'Share of shown banners that got a decision',
  'Ø Entscheidungszeit': 'Median decision time',
  '{s} s': '{s} s',
  'Median · p95 {s} s': 'Median · p95 {s} s',
  'noch keine Messung': 'no measurement yet',
  'Entscheidungen pro Tag': 'Decisions per day',
  Trichter: 'Funnel',
  Seitenaufrufe: 'Pageviews',
  Interaktion: 'Interaction',
  Entscheidung: 'Decision',
  '{n} Fehlerereignisse im Zeitraum.': '{n} error events in this period.',
  'Erlaubte Kategorien': 'Allowed categories',
  'Anteil der Entscheidungen, in denen die Kategorie erlaubt wurde.':
    'Share of decisions in which the category was allowed.',
  Länder: 'Countries',
  'Die Länderverteilung': 'The country breakdown',
  Geräte: 'Devices',
  'Die Geräteverteilung': 'The device breakdown',
  'Keine Daten im Zeitraum.': 'No data in this period.',
  'Entscheidungen pro Tag, gestapelt nach Art':
    'Decisions per day, stacked by kind',
  '{day}: {n} Entscheidungen': '{day}: {n} decisions',
  'Alle akzeptiert': 'Accepted all',
  'Individuell gespeichert': 'Saved individually',
  'Alle abgelehnt': 'Rejected all',
  'Spitze: {n}/Tag': 'Peak: {n}/day',
  '{pct} Einwilligung': '{pct} consent',

  // ── Team (routes/Team.tsx) ───────────────────────────────────────────────────────
  'Rückfall-Regime': 'Fallback regime',
  'Das Rückfall-Regime': 'The fallback regime',
  'Rückfall-Regime geändert. Es gilt ab dem nächsten Veröffentlichen.':
    'Fallback regime changed. It applies from the next publish onwards.',
  'Gilt für Besucher, deren Land sich nicht bestimmen lässt — zurzeit für alle, weil pro Website nur eine Variante ausgeliefert wird. Eine Änderung wirkt ab dem nächsten Veröffentlichen der jeweiligen Website.':
    'Applies to visitors whose country cannot be determined — currently to all of them, because only one variant is served per website. A change takes effect from that website\u2019s next publish.',
  'Nur die Inhaberin oder der Inhaber des Kontos kann das ändern.':
    'Only the account owner can change this.',
  'Ohne Regime ausliefern?': 'Serve without a regime?',
  'Trotzdem setzen': 'Set it anyway',
  'Besucher, deren Land unbekannt ist, werden dann ohne Einwilligungspflicht behandelt — es wird nichts vorab blockiert. Innerhalb der EU ist das in aller Regel falsch.':
    'Visitors whose country is unknown are then treated as needing no consent — nothing is blocked up front. Inside the EU that is almost always wrong.',
  Mitglieder: 'Members',
  'Die Mitgliederliste': 'The member list',
  Sie: 'You',
  'Alle Websites': 'All websites',
  'zuletzt {date}': 'last {date}',
  'noch nie angemeldet': 'never signed in',
  'Mitglied entfernen?': 'Remove member?',
  '{who} verliert sofort den Zugang zu allen Websites dieses Kontos. Eine neue Einladung stellt ihn wieder her.':
    '{who} immediately loses access to every website in this account. A new invitation restores it.',
  'Mitglied entfernt.': 'Member removed.',
  'Rolle geändert.': 'Role changed.',
  'Offene Einladungen': 'Open invitations',
  '{role} · läuft ab {date}': '{role} · expires {date}',
  'Einladung zurückziehen?': 'Revoke invitation?',
  Zurückziehen: 'Revoke',
  'Der Link an {email} funktioniert danach nicht mehr.':
    'The link sent to {email} will stop working.',
  'Einladung zurückgezogen.': 'Invitation revoked.',
  Einladen: 'Invite',
  'Einladungslink für {email} — er wird nur dieses eine Mal angezeigt und ist sieben Tage gültig.':
    'Invitation link for {email} — shown this once, valid for seven days.',
  'Link kopieren': 'Copy link',
  'Ich habe ihn gespeichert': 'I have saved it',
  'E-Mail': 'Email',
  Rolle: 'Role',
  'Nichts auswählen heißt alle — auch die, die es noch nicht gibt.':
    'Selecting none means all of them — including ones that do not exist yet.',
  'API-Schlüssel': 'API keys',
  'Für Skripte und Integrationen. Ein Schlüssel gehört dem Tenant, nicht einer Person — deshalb kann er nur lesen und auswerten, nie Zugänge verändern.':
    'For scripts and integrations. A key belongs to the tenant, not to a person — so it can only read and analyse, never change access.',
  '{name} — dieser Schlüssel wird jetzt einmal angezeigt und ist danach nicht mehr auslesbar.':
    '{name} — this key is displayed once now and cannot be read out afterwards.',
  'Schlüssel kopieren': 'Copy key',
  'nie benutzt': 'never used',
  'Schlüssel widerrufen?': 'Revoke key?',
  'Jede Integration, die {name} benutzt, bekommt ab sofort 401. Ein widerrufener Schlüssel lässt sich nicht reaktivieren.':
    'Every integration using {name} gets a 401 from now on. A revoked key cannot be reactivated.',
  'Schlüssel widerrufen.': 'Key revoked.',
  'Wofür ist er da? Das steht später in der Liste.':
    'What is it for? That is what the list shows later.',
  'Reporting-Skript': 'Reporting script',
  'Auswertung — lesen und auswerten': 'Analyst — read and analyse',
  'Integration — nur Websites und Statistik': 'Integration — websites and statistics only',
  Erstellen: 'Create',
  'Der letzte Owner kann nicht entfernt oder herabgestuft werden — danach könnte niemand mehr jemanden ernennen.':
    'The last owner cannot be removed or demoted — after that nobody could appoint anyone.',
  'Diese Person ist bereits im Team.': 'This person is already in the team.',
  'Diese Rolle können Sie nicht vergeben.': 'You cannot grant this role.',
  'Die E-Mail-Adresse sieht nicht richtig aus.': 'That email address does not look right.',

  // ── Login and invitation (routes/Login.tsx, routes/AcceptInvite.tsx) ─────────────
  'Bitte melden Sie sich an.': 'Please sign in.',
  Passwort: 'Password',
  Anmelden: 'Sign in',
  'Anmelden …': 'Signing in …',
  'E-Mail oder Passwort ist falsch.': 'That email or password is wrong.',
  'Anmeldung fehlgeschlagen. Läuft das Backend?': 'Sign-in failed. Is the backend running?',
  'Diese Einladung ist nicht (mehr) gültig. Bitten Sie um eine neue.':
    'This invitation is no longer valid. Ask for a new one.',
  'Konto angelegt. Melden Sie sich jetzt an.': 'Account created. Sign in now.',
  'Zur Anmeldung': 'Go to sign-in',
  '{tenant} lädt Sie als {email} ein.': '{tenant} is inviting you as {email}.',
  'Für diese Adresse gibt es bereits ein Konto. Nach dem Annehmen melden Sie sich wie gewohnt an.':
    'There is already an account for this address. After accepting, sign in as usual.',
  'Mindestens 12 Zeichen.': 'At least 12 characters.',
  'Das Passwort braucht mindestens 12 Zeichen.': 'The password needs at least 12 characters.',
  'Die Einladung konnte nicht angenommen werden. Vielleicht ist sie abgelaufen.':
    'The invitation could not be accepted. It may have expired.',
  'Wird angenommen …': 'Accepting …',
  'Einladung annehmen': 'Accept invitation',

  // ── Setup checklist (features/setup/SetupChecklist.tsx) ──────────────────────────
  'Einrichtung — noch {n} Schritt': 'Setup — {n} step left',
  'Einrichtung — noch {n} Schritte': 'Setup — {n} steps left',
  'Website angelegt': 'Website created',
  '{domain} · Schlüssel {key}': '{domain} · key {key}',
  'Banner-Text geprüft': 'Banner text reviewed',
  'Es gibt einen Titel und einen Text. Beides steht später im Nachweis.':
    'There is a title and a body. Both end up in the proof.',
  'Das Banner hat noch keinen Text.': 'The banner has no text yet.',
  'Zum Banner': 'Go to banner',
  Veröffentlicht: 'Published',
  'Besucher bekommen diese Fassung ausgeliefert.': 'Visitors are served this version.',
  'Solange nichts veröffentlicht ist, arbeitet das SDK mit seinen eingebauten Notfall-Regeln.':
    'Until something is published, the SDK runs on its built-in emergency rules.',
  'Snippet eingebaut': 'Snippet installed',
  'Noch nicht geprüft.': 'Not checked yet.',
  'wird geprüft …': 'checking …',
  'Die Seite war nicht erreichbar: {error}': 'The page was not reachable: {error}',
  'Auf der Startseite wurde kein Loader gefunden.': 'No loader was found on the home page.',
  'Es lädt ein Loader, aber mit einem anderen Site-Schlüssel.':
    'A loader is loading, but with a different site key.',
  'Der Loader wurde gefunden und das Banner ist erschienen.':
    'The loader was found and the banner appeared.',
  'Trotz Banner wurden vor der Einwilligung Anbieter geladen: {vendors}. Meist steht das Snippet zu weit unten im <head>.':
    'Vendors loaded before consent despite the banner: {vendors}. Usually the snippet sits too low in the <head>.',
  'Das Snippet': 'The snippet',
  'So weit oben im <head> wie möglich. Alles, was davor steht, kann das SDK nicht mehr anhalten — und genau das wäre der Verstoß, den es verhindern soll.':
    'As high in the <head> as possible. Anything above it is beyond the SDK\'s reach — and that is exactly the violation it exists to prevent.',

  // ── New-site wizard (features/setup/NewSiteWizard.tsx) ───────────────────────────
  'Neue Website': 'New website',
  'Schritt {i} von {n}': 'Step {i} of {n}',
  'Später weitermachen': 'Continue later',
  'Live schalten': 'Go live',
  'Banner-Text': 'Banner text',
  'Ohne https:// und ohne Pfad — nur der Host.': 'No https:// and no path — the host only.',
  'Nur für Sie. Taucht auf der Website nicht auf.':
    'For you alone. It does not appear on the website.',
  'Diese Domain gehört bereits zu einer anderen Website.':
    'This domain already belongs to another website.',
  'Bitte nur die Domain, ohne https:// und ohne Pfad.':
    'The domain only, please — no https:// and no path.',
  'Pflichtfeld.': 'Required.',
  'Anlegen fehlgeschlagen.': 'Creating it failed.',
  'Wird angelegt …': 'Creating …',
  Weiter: 'Continue',
  Überspringen: 'Skip',
  'Nur eingetragene Hosts dürfen das Banner laden. {primary} ist gesetzt. Fehlt {twin} oder eine Testumgebung, erscheint dort kein Banner — das ist mit Abstand die häufigste Ursache dafür, dass es „nicht funktioniert“.':
    'Only listed hosts may load the banner. {primary} is set. If {twin} or a test environment is missing, no banner appears there — by far the most common reason for "it does not work".',
  'die Variante ohne www': 'the non-www variant',
  'die www-Variante': 'the www variant',
  'Weitere Domain {n}': 'Additional domain {n}',
  'Weitere Domain': 'Another domain',
  'Wird gespeichert …': 'Saving …',
  'Mindestens eine Domain wurde nicht übernommen. Sie können sie später unter „Blockieren & Anbieter“ nachtragen.':
    'At least one domain was not taken over. You can add it later under "Blocking & vendors".',
  'Das gilt für Besucher, deren Land sich nicht bestimmen lässt. Für alle anderen entscheidet ihr Aufenthaltsort — strenger, nie lockerer.':
    'This applies to visitors whose country cannot be determined. For everyone else their location decides — stricter, never laxer.',
  'Die Rechtslage': 'The legal regime',
  'Ermittelt aus': 'Derived from',
  'Dieser Wortlaut wird bei jeder Einwilligung mitgespeichert. Alles Weitere — Farben, Kategorien, Anbieter — steht danach im Editor.':
    'This wording is stored with every consent. Everything else — colours, categories, vendors — is in the editor afterwards.',
  'Abgelehnt: {details}': 'Refused: {details}',
  'Der Text konnte nicht gespeichert werden.': 'The text could not be saved.',
  '1. Veröffentlichen': '1. Publish',
  '2. Snippet einbauen': '2. Install the snippet',
  '3. Prüfen': '3. Check',
  'Solange nichts veröffentlicht ist, liefert das CDN nichts aus und das SDK arbeitet mit seinen eingebauten Notfall-Regeln.':
    'Until something is published the CDN serves nothing and the SDK runs on its built-in emergency rules.',
  'Wird veröffentlicht …': 'Publishing …',
  'Die Konfiguration hat noch offene Probleme. Der Editor zeigt welche.':
    'The configuration still has open problems. The editor shows which.',
  'Ihre Rolle darf nicht veröffentlichen. Bitten Sie Admin oder Inhaber darum.':
    'Your role may not publish. Ask an admin or an owner.',
  'Veröffentlichen fehlgeschlagen.': 'Publishing failed.',
  'So weit oben im <head> wie möglich. Alles, was davor steht, kann das SDK nicht mehr anhalten.':
    'As high in the <head> as possible. Anything above it is beyond the SDK\'s reach.',
  'Einbau prüfen': 'Check installation',
  'Trotz Banner wurden vor der Einwilligung Anbieter geladen: {vendors}. Meist steht das Snippet zu weit unten.':
    'Vendors loaded before consent despite the banner: {vendors}. Usually the snippet sits too low.',
  'Fertig — zur Website': 'Done — go to the website',

  // ── Undo labels (features/banner/store/draftStore.ts), shown as "Undo: …" ────────
  'Text geändert': 'text changed',
  'Layout geändert': 'layout changed',
  'Überschrift geändert': 'heading changed',
  'Kategorietext geändert': 'category text changed',
  'Text der Auswahl geändert': 'preferences text changed',
  'Button-Reihenfolge geändert': 'button order changed',
  'Button-Reihenfolge im Auswahl-Dialog geändert': 'button order in the preferences dialog changed',
  'Abstand geändert': 'spacing changed',
  'Anbieterliste geändert': 'vendor list changed',
  'Breite geändert': 'width changed',
  'Cookie-Namen geändert': 'cookie names changed',
  'Einblendung geändert': 'entrance changed',
  'Escape-Verhalten geändert': 'escape behaviour changed',
  'Hintergrundklick geändert': 'background click changed',
  'Innenabstand geändert': 'padding changed',
  'Overlay geändert': 'overlay changed',
  'Schließen-Aktion geändert': 'dismiss action changed',
  'Schließen-Kreuz geändert': 'close cross changed',
  'Seite sperren geändert': 'page lock changed',
  'Button geändert': 'button changed',
  'Button-Anordnung geändert': 'button arrangement changed',
  'Buttonbreite geändert': 'button width changed',
  'Dialog-Darstellung geändert': 'dialog presentation changed',
  'Logo geändert': 'logo changed',
  'Kategorie geändert': 'category changed',
  'Kategorien umsortiert': 'categories reordered',
  'Kategorie hinzugefügt': 'category added',
  'Kategorie entfernt': 'category removed',
  'Links geändert': 'links changed',
  'Badge geändert': 'badge changed',
  'Sprachen geändert': 'languages changed',

  // Remaining editor controls (checkbox and option labels).
  Schatten: 'Shadow',
  'Hausschrift …': 'House font …',
  'Ablehnen gleich gewichtet wie Akzeptieren': 'Reject weighted equally with accept',
  'Unter der DSGVO erwartet. Ohne diese Gleichbehandlung ist das Banner angreifbar.':
    'Expected under the GDPR. Without this equal treatment the banner is open to challenge.',
  'Schwebendes Badge': 'Floating badge',
  'Logo entfernen': 'Remove logo',
  'Hintergrund abdunkeln': 'Dim the background',
  'Seite sperren, bis entschieden wurde': 'Lock the page until a decision is made',
  'Ohne Sperre ist das Overlay nur Optik — Klicks gehen hindurch.':
    'Without the lock the overlay is decoration only — clicks pass through it.',
  'Auch die Cookie-Namen zeigen': 'Show cookie names too',
  'Eigene Farbe': 'Custom colour',
  Aus: 'Off',

  // ── Preview frame (features/banner/preview/BannerFrame.tsx) ─────────────────────
  'Vorschau — Klicks werden nicht gespeichert': 'Preview — clicks are not saved',
  'Vorschau wird gerendert …': 'Rendering preview …',
  Kundenshop: 'Client shop',

  // ── Banner preview iframe (features/banner/preview/BannerFrame.tsx) ─────────────
  'Vorschau-Fehler: ': 'Preview error: ',
  'ui.js konnte nicht geladen werden.': 'ui.js could not be loaded.',

  // ── Primary domain (features/blocking/DomainsCard.tsx) ──────────────────────────
  'Als Hauptdomain festlegen': 'Make primary domain',
  'Die Hauptdomain konnte nicht gewechselt werden.': 'The primary domain could not be changed.',
  'Die Hauptdomain ist die Adresse dieser Website: Scans starten dort, der Einbau-Test prüft dort, und die Cookie-Erklärung nennt sie. Eine Testumgebung kann sie nicht sein — von dort kommen keine Ereignisse.':
    'The primary domain is this website\'s address: scans start there, the installation check looks there, and the cookie declaration names it. A test environment cannot be it — no events come from there.',

  // ── Scan screen: page limit and progress (features/scan/ScanScreen.tsx) ─────────
  'Seiten höchstens': 'pages at most',
  'Die Zahl begrenzt, wie viele Seiten eine Runde besucht. Leer lassen für die Voreinstellung.':
    'The number caps how many pages one pass visits. Leave empty for the default.',
  'In der Warteschlange …': 'Queued …',
  'Scan läuft …': 'Scan running …',
  'Fortschritt des Scans': 'Scan progress',
  'Diese Seitenzahl akzeptiert der Server nicht. Lassen Sie das Feld leer für die Voreinstellung.':
    'The server will not accept that page count. Leave the field empty for the default.',
  '{n} von höchstens {max} Seite': '{n} of at most {max} page',
  '{n} von höchstens {max} Seiten': '{n} of at most {max} pages',

  // ── Banner fonts, loaded from the customer's own site (PropsPanel.tsx) ──────────
  'Das Banner lädt eine Schrift nur von Ihrer eigenen Website. Von dort ist es dieselbe Herkunft wie die Seite selbst. Ein Webfont von Google Fonts oder einem fremden CDN wäre dagegen ein Aufruf bei einem Dritten, bevor eingewilligt wurde — genau das, was das Banner verhindert.':
    'The banner loads a typeface only from your own website. From there it is the same origin as the page itself. A webfont from Google Fonts or someone else\'s CDN would be a call to a third party before consent — precisely what the banner prevents.',
  Fließtext: 'Body text',
  'Name der Schriftfamilie': 'Font family name',
  'Adresse der Schriftdatei': 'Address of the font file',
  'Am besten ein Pfad ohne Domain: der ist auf jeder Adresse Ihrer Website dieselbe Herkunft — auch auf www und auf der Testumgebung.':
    'Best given as a path without a domain: that is the same origin on every address your website answers on — including www and the test environment.',
  'Ein Pfad wie /fonts/brand.woff2 oder eine vollständige https-Adresse.':
    'A path such as /fonts/brand.woff2, or a full https address.',
  '{host} gehört nicht zu den Domains dieser Website. Das Banner würde diese Schrift bei einem Dritten anfragen, bevor eingewilligt wurde. Ein Pfad wie /fonts/brand-sans.woff2 vermeidet das.':
    '{host} is not one of this website\'s domains. The banner would request this typeface from a third party before consent. A path such as /fonts/brand-sans.woff2 avoids that.',
  'Beides nötig: ohne Namen wird die Datei nicht verwendet, ohne Adresse nicht geladen. Veröffentlichen schlägt sonst fehl.':
    'Both are needed: without a name the file is not used, without an address it is not loaded. Publishing fails otherwise.',
  'Schrift entfernen': 'Remove typeface',
  'Der Name der Familie, so wie Ihre Seite sie lädt. Immer eine Ausweichschrift dahinter — sie entscheidet, solange eine Schriftdatei unten noch lädt, und auf einem Gerät, wo diese nie ankommt.':
    'The family name exactly as your page loads it. Always a fallback behind it — it draws the text while a font file below is still on its way, and permanently on any device where it never arrives.',

  // ── Publish-blocking issues for fonts (Builder.tsx ISSUE_TEXT) ──────────────────
  'Nur ein Schriftname — die Adresse der Schriftdatei gehört in das Feld darunter.':
    'A font name only — the address of the font file belongs in the field below.',
  'Eine Schrift braucht beides: Familienname und Adresse.':
    'A typeface needs both: family name and address.',

  // ── Publishing a site for the first time (Builder.tsx) ─────────────────────────
  'Erstmals veröffentlichen': 'Publish for the first time',
  'Diese Website war noch nie veröffentlicht — Besucher sehen bisher nichts von dieser Konfiguration.':
    'This website has never been published — visitors see nothing of this configuration yet.',

  // ── Vendors waiting on a scan decision (BlockingScreen.tsx) ────────────────────
  'Aus dem Scan wartet {n} Anbieter auf eine Entscheidung.':
    '{n} vendor from the scan is waiting for a decision.',
  'Aus dem Scan warten {n} Anbieter auf eine Entscheidung.':
    '{n} vendors from the scan are waiting for a decision.',
  'Zum Scan': 'Go to the scan',
  'Noch keiner übernommen — entschieden wird im Scan.':
    'None adopted yet — the decision is made in the scan.',

  // ── Publish diff, generic path labels (features/banner/publish/diff.ts) ────────
  Button: 'Button',
  sichtbar: 'visible',
  'im Auswahl-Dialog ausgeblendet': 'hidden in the preferences dialog',
  'Wenn der Auswahl-Dialog „Alle akzeptieren“ zeigt, muss er auch „Alle ablehnen“ zeigen.':
    'If the preferences dialog offers “Accept all”, it has to offer “Reject all” too.',
  Stil: 'style',
  'Kategorie {slug} · {field}': 'Category {slug} · {field}',
  '{n} Anbieter sind gepflegt, aber der Dialog nennt keinen davon. Art. 13 verlangt, dass Empfänger benannt werden — einzuschalten unter „Auswahl-Dialog“.':
    '{n} vendors are on file, but the dialog names none of them. Art. 13 requires recipients to be named — switch it on under “Selection dialog”.',
  'Breite auf Mobilgeräten':
    'Width on mobile',
  'Unter 640 px Bildschirmbreite. Der Bildschirm begrenzt weiterhin — breiter als das Gerät wird das Banner nicht.':
    'Below 640px of screen width. The screen still caps it — the banner never gets wider than the device.',
  'Unter 640 px Bildschirmbreite füllt das Banner die Breite aus, mit 16 px Rand je Seite. Das ist die Voreinstellung.':
    'Below 640px of screen width the banner fills the width, with a 16px margin on each side. This is the default.',
  'Feste Breite auf Mobilgeräten':
    'Fixed width on mobile',

  // ── Per-site settings (features/settings/SettingsScreen.tsx) ───────────────────
  Allgemein: 'General',
  Scan: 'Scan',
  'Nur für die Konsole — Besucher sehen ihn nie.': 'For the console only — visitors never see it.',
  'Name geändert.': 'Name changed.',
  'Der Name braucht mindestens ein und höchstens 120 Zeichen.':
    'The name needs at least one and at most 120 characters.',
  'Wöchentlicher Scan': 'Weekly scan',
  'API-Schlüssel, Rückfall-Regime (ganzes Konto)': 'API keys, fallback regime (whole account)',
  Gefahrenzone: 'Danger zone',
  'Website löschen': 'Delete website',
  'Endgültig löschen': 'Delete permanently',
  'Nach dem Löschen wird die Konfiguration nicht mehr ausgeliefert: das Snippet auf {domain} blockiert dann alles nach seinen Startregeln, bis Sie es entfernen. Nachweise und Protokoll bleiben erhalten.':
    'Once deleted, the configuration is no longer served: the snippet on {domain} then blocks everything by its seed rules until you remove it. Proofs and the audit trail are kept.',
  'Tippen Sie {domain}, um zu bestätigen.': 'Type {domain} to confirm.',
  'Danach verschwindet die Website aus der Übersicht und ihre Auswertung ist nicht mehr erreichbar.':
    'After that the website disappears from the list and its analytics are no longer reachable.',

  // ── Countries tile with no geo source at all (features/stats/StatsScreen.tsx) ───
  'Für alle {n} Auslieferungen ist kein Land bekannt.': 'No country is known for any of the {n} deliveries.',
  'Das Land kommt aus dem Header des Proxys vor dem Ingest (CF-IPCountry oder X-Geo-Country), nie aus dem Browser. Fehlt der Header, bleibt diese Verteilung leer — und für die Rechtslage gilt das Rückfall-Regime des Kontos, also die strengere Annahme.':
    'The country comes from the header set by the proxy in front of the ingest (CF-IPCountry or X-Geo-Country), never from the browser. Without that header this breakdown stays empty — and the legal regime falls back to the account default, which is the stricter assumption.',
};
